/**
 * Document types supported by the ipfs-document module (see IPFS_DOCUMENT_MODULE.md §8).
 * Values must match the backend `DocumentType` enum values exactly — those are what go over the
 * wire. (Note the backend key for the covenant grant is misspelled `GRANT_ECO_COVANANT`, but its
 * value — the part that matters — is `GRANT_ECO_COVENANT`.)
 */
export enum DocumentType {
  MASTER_ARCHITECTURE = 'MASTER_ARCHITECTURE',
  PATRON_IDENTITY = 'PATRON_IDENTITY',
  NOTICE_OF_CHALLENGE = 'NOTICE_OF_CHALLENGE',
  VERIFIER_ENGAGEMENT = 'VERIFIER_ENGAGEMENT',
  OPERATING_AGREEMENT = 'OPERATING_AGREEMENT',
  GRANT_ECO_COVENANT = 'GRANT_ECO_COVENANT',
  CONSERVATION_TRUST_DEED = 'CONSERVATION_TRUST_DEED',
  DEED_OF_APPOINTMENT_OF_SUB_ATTORNEY = 'DEED_OF_APPOINTMENT_OF_SUB_ATTORNEY',
  DEED_OF_ASSIGNMENT = 'DEED_OF_ASSIGNMENT',
  DEED_OF_ECOLOGICAL_COVENANT = 'DEED_OF_ECOLOGICAL_COVENANT',
}

/**
 * The protocol version the role instruments (Verifier Engagement, Operating Agreement) are signed
 * under — their own header reads "EcoFutures Master Architecture v11.5". Fills their `VERSION`.
 */
export const PROTOCOL_TERMS_VERSION = '11.5';

/**
 * Types whose every required signer is always wet-ink — there is no wallet signing on these, and
 * the creator is not automatically added as a signer. This is a frontend/product rule; the backend
 * does NOT enforce it (method is per-signer server-side), so it lives only in the create UI.
 */
export const ALWAYS_WET_INK_TYPES: DocumentType[] = [
  DocumentType.GRANT_ECO_COVENANT,
  DocumentType.CONSERVATION_TRUST_DEED,
  DocumentType.DEED_OF_APPOINTMENT_OF_SUB_ATTORNEY,
  DocumentType.DEED_OF_ASSIGNMENT,
  DocumentType.DEED_OF_ECOLOGICAL_COVENANT,
];

export const isAlwaysWetInk = (type: DocumentType | string): boolean =>
  (ALWAYS_WET_INK_TYPES as string[]).includes(type as string);

/** Selectable list for the create form — order is the order shown in the dropdown. */
export const DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.MASTER_ARCHITECTURE,
  DocumentType.PATRON_IDENTITY,
  DocumentType.NOTICE_OF_CHALLENGE,
  DocumentType.VERIFIER_ENGAGEMENT,
  DocumentType.OPERATING_AGREEMENT,
  DocumentType.GRANT_ECO_COVENANT,
  DocumentType.CONSERVATION_TRUST_DEED,
  DocumentType.DEED_OF_APPOINTMENT_OF_SUB_ATTORNEY,
  DocumentType.DEED_OF_ASSIGNMENT,
  DocumentType.DEED_OF_ECOLOGICAL_COVENANT,
];

/** Per-signer signing method — assigned per required signer, not per document (see wet-ink guide §1). */
export enum SigningMethod {
  WALLET = 'WALLET',
  WET_INK = 'WET_INK',
}

/** Stored Gemini verdict returned with a WET_INK signature (see wet-ink guide §7). */
export interface WetInkVerification {
  tampered: boolean;
  confidence?: number;
  summary?: string;
  discrepancies?: string[];
}

export interface IpfsDocumentSignature {
  id: number;
  stakeholderWallet: string;
  stakeholderName: string;
  stakeholderRole: string;
  /** `"WALLET"` | `"WET_INK"`. Absent on older records defaults to WALLET. */
  method?: SigningMethod | 'WALLET' | 'WET_INK';
  /** Only populated for WALLET signatures — `null` for WET_INK. */
  signatureCid: string | null;
  qrCodeBase64?: string | null;
  /** Only populated for WET_INK signatures — the pinned evidence scan. */
  wetInkScanCid?: string | null;
  wetInkScanGcsPath?: string | null;
  wetInkVerification?: WetInkVerification | null;
  signedAt: string;
}

export interface IpfsDocument {
  id: number;
  type: DocumentType | string;
  status: 'pending_signatures' | 'completed';
  /** `<<TOKEN>>` → value map. Keys absent here are still unfilled. */
  templateVars: Record<string, string>;
  /** Maps each filled token key to the lowercase wallet that filled it. */
  templateVarOwners: Record<string, string>;
  /** Lowercase wallet of the admin who created the document. */
  creatorAddress: string;
  requiredSigners: string[];
  /**
   * Per-signer assigned method, keyed by lowercase signer identifier. Wallets absent here default
   * to WALLET. This is where a signer's *assigned* method lives — a signature record only exists
   * once they've actually signed.
   */
  signerMethods?: Record<string, SigningMethod | 'WALLET' | 'WET_INK'>;
  finalCid: string | null;
  finalGatewayUrl: string | null;
  finalGcsPath: string | null;
  createdAt: string;
  updatedAt: string;
  signatures: IpfsDocumentSignature[];
}

export interface IpfsDownloadUrls {
  ipfsUrl: string;
  gcsUrl: string;
}

/** Response of `GET /ipfs-document/templates/:type`. */
export interface TemplatePlaceholders {
  type: string;
  placeholders: string[];
}
